import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider, CssBaseline } from "@mui/material";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import theme from "./theme";
import { tokens } from "./api";
import Landing from "./pages/Landing";
import Account from "./pages/Account";
import Profile from "./pages/Profile";
import Plans from "./pages/Plans";
import Checkout from "./pages/Checkout";
import AdminLogin from "./pages/AdminLogin";
import AdminAccounts from "./pages/AdminAccounts";
import AdminChatHistory from "./pages/AdminChatHistory";
import AdminLicences from "./pages/AdminLicences";
import AdminCoupons from "./pages/AdminCoupons";
import AdminAccountDetail from "./pages/AdminAccountDetail";

/*
 * Two guards, because the two sessions are separate. A customer landing on /admin is sent
 * to the staff login, not to their own account page - the two are different doors into
 * the same building and conflating them is how a customer ends up staring at an admin URL.
 */
const RequireAccount = ({ children }) => tokens.account.get() ? children : <Navigate to="/" replace />;
const RequireConsole = ({ children }) => tokens.console.get() ? children : <Navigate to="/admin" replace />;

export default function App() {
    // Empty in development until the OAuth client exists; the Google button simply does
    // not render, and the OTP route still works.
    const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";

    return (
        <GoogleOAuthProvider clientId={googleClientId}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <BrowserRouter>
                    <Routes>
                        {/* Someone already signed in goes straight to their account; everybody
                            else gets the landing page, and reaches the form from there. */}
                        <Route path="/" element={tokens.account.get() ? <Navigate to="/account" replace /> : <Landing />} />
                        {/* Signing in lives on the landing page now. The old paths are kept
                            because plants link to them with ?email=…&plant=…, and that link is
                            printed in builds already out on customer hardware. */}
                        <Route path="/register" element={<Landing />} />
                        <Route path="/login" element={<Landing />} />
                        <Route path="/account" element={<RequireAccount><Account /></RequireAccount>} />
                        <Route path="/profile" element={<RequireAccount><Profile /></RequireAccount>} />
                        <Route path="/plans" element={<RequireAccount><Plans /></RequireAccount>} />
                        <Route path="/checkout" element={<RequireAccount><Checkout /></RequireAccount>} />

                        <Route path="/admin" element={<AdminLogin />} />
                        <Route path="/admin/accounts" element={<RequireConsole><AdminAccounts /></RequireConsole>} />
                        <Route path="/admin/licences" element={<RequireConsole><AdminLicences /></RequireConsole>} />
                        <Route path="/admin/coupons" element={<RequireConsole><AdminCoupons /></RequireConsole>} />
                        <Route path="/admin/chats" element={<RequireConsole><AdminChatHistory /></RequireConsole>} />
                        <Route path="/admin/accounts/:id" element={<RequireConsole><AdminAccountDetail /></RequireConsole>} />

                        <Route path="*" element={<Navigate to="/" replace />} />
                    </Routes>
                </BrowserRouter>
                <ToastContainer position="top-right" autoClose={3000} newestOnTop />
            </ThemeProvider>
        </GoogleOAuthProvider>
    );
}
